import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, filter, finalize, take } from 'rxjs';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import { ErrorhandleService } from 'src/app/core/services/error-handling/error-handle/errorhandle.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { environment } from 'src/app/environments/environment';
import { CvisSwap } from '../models/cvis-swap.model';
import {
  DeleteEdiSwapResponse,
  GetAllEdiSwapsResponse,
} from '../models/edi-swap.model';

@Injectable({
  providedIn: 'root',
})
export class EdiSwapService {
  private readonly http = inject(HttpClient);
  private readonly commonUtils = inject(CommonUtils);
  private readonly errorHandler = inject(ErrorhandleService);

  private readonly baseUrl = environment.apiUrl.concat(
    '/sci-edi-swap-service/edi-swap/',
  );

  getEdiSwap(): Observable<GetAllEdiSwapsResponse> {
    this.commonUtils.updateLoadingModal(true);
    this.commonUtils.lockUpdateLoadingModal(true);

    return this.http.get<GetAllEdiSwapsResponse>(this.baseUrl, {}).pipe(
      take(1),
      catchError((error) =>
        this.errorHandler.handleError<GetAllEdiSwapsResponse>(
          error,
          error.status,
          'SCI EDI Swap service: Get All Edi Swaps',
          BANNER_TYPE.TABLE_DATA_ERROR,
        ),
      ),
      finalize(() => {
        this.commonUtils.lockUpdateLoadingModal(false);
        this.commonUtils.updateLoadingModal(false);
      }),
    );
  }

  addEdiSwap(data: CvisSwap) {
    this.commonUtils.updateLoadingModal(true);
    return this.http.post(this.baseUrl.concat(''), data).pipe(
      take(1),
      catchError((error) =>
        this.errorHandler.handleError<void>(
          error,
          error.status,
          'Edi Swap Service: Add Edi Swap',
          BANNER_TYPE.API_400_ERROR,
        ),
      ),
      finalize(() => {
        this.commonUtils.updateLoadingModal(false);
      }),
    );
  }

  editEdiSwap(data: CvisSwap) {
    this.commonUtils.updateLoadingModal(true);
    return this.http.put(this.baseUrl.concat(''), data).pipe(
      take(1),
      catchError((error) =>
        this.errorHandler.handleError<void>(
          error,
          error.status,
          'Edi Swap Service: Edit Edi Swap',
          BANNER_TYPE.API_400_ERROR,
        ),
      ),
      finalize(() => {
        this.commonUtils.updateLoadingModal(false);
      }),
    );
  }

  deleteEdi(ediId: number): Observable<DeleteEdiSwapResponse> {
    this.commonUtils.updateLoadingModal(true);
    const url = `${this.baseUrl}?ediSwapId=${ediId}`;

    return this.http.delete<DeleteEdiSwapResponse>(url).pipe(
      take(1),
      catchError((error) =>
        this.errorHandler.handleError<DeleteEdiSwapResponse>(
          error,
          error.status,
          'Edi Swap Service: Delete Edi',
          BANNER_TYPE.API_400_ERROR,
        ),
      ),
      filter((value) => !!value),
      finalize(() => {
        this.commonUtils.updateLoadingModal(false);
      }),
    );
  }

  uploadSwap(file: File): Observable<DeleteEdiSwapResponse> {
    this.commonUtils.updateLoadingModal(true);
    const url = `${this.baseUrl}file-upload`;

    const formData = new FormData();
    formData.append('file', file, file.name);

    return this.http.post<DeleteEdiSwapResponse>(url, formData).pipe(
      take(1),
      catchError((error) =>
        this.errorHandler.handleError<DeleteEdiSwapResponse>(
          error,
          error.status,
          'Edi Swap Service: File upload',
          BANNER_TYPE.API_400_ERROR,
        ),
      ),
      filter((value) => !!value),
      finalize(() => {
        this.commonUtils.updateLoadingModal(false);
      }),
    );
  }
}
